import { useState, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { SERVER_URL } from '../config';
import Avatar from './Avatar';

/**
 * ProfilePhotoEditor
 *
 * Render-prop component that handles profile photo upload/remove logic.
 * Renders a modal for preview + save/cancel, and exposes openPicker via children.
 *
 * Usage:
 *   <ProfilePhotoEditor onSuccess={refreshUser}>
 *     {({ openPicker }) => <button onClick={openPicker}>Edit Photo</button>}
 *   </ProfilePhotoEditor>
 */
function ProfilePhotoEditor({ onSuccess, currentPhotoUrl, userName, userEmail, children }) {
  const { user, token } = useAuth();
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);

  const photoUrl = currentPhotoUrl ?? user?.profile_photo_url;
  const name = userName ?? user?.username;
  const email = userEmail ?? user?.email;

  const openModal = () => {
    setError('');
    setShowModal(true);
  };

  const openPicker = () => {
    setError('');
    fileInputRef.current?.click();
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Please upload a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB.');
      setShowModal(true);
      return;
    }

    setPendingFile(file);
    setPendingPreview(URL.createObjectURL(file));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!pendingFile) return;
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', pendingFile);

      const uploadRes = await fetch(`${SERVER_URL}/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const { image_url } = await uploadRes.json();

      const updateRes = await fetch(`${SERVER_URL}/users/${user.id}/profile-photo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ profile_photo_url: image_url }),
      });
      if (!updateRes.ok) throw new Error('Failed to update photo');

      onSuccess && onSuccess(`${image_url}?t=${Date.now()}`);
      handleClose();
    } catch (err) {
      setError('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setUploading(true);
    setError('');

    try {
      const res = await fetch(`${SERVER_URL}/users/${user.id}/profile-photo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ profile_photo_url: null }),
      });
      if (!res.ok) throw new Error('Failed to remove photo');

      onSuccess && onSuccess(null);
      handleClose();
    } catch (err) {
      setError('Failed to remove photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setPendingFile(null);
    setPendingPreview(null);
    setError('');
    setShowModal(false);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      {children({ openPicker, openModal })}

      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Update Profile Photo</h2>

            <div className="flex flex-col items-center gap-4 mb-6">
              {pendingPreview ? (
                <img
                  src={pendingPreview}
                  alt="Preview"
                  className="w-28 h-28 rounded-full object-cover border-2 border-indigo-400"
                />
              ) : (
                <Avatar name={name} email={email} profilePhotoUrl={photoUrl} size="w-28 h-28" textSize="text-3xl" />
              )}
              <button
                onClick={openPicker}
                className="text-sm text-indigo-600 hover:underline"
              >
                Choose a different photo
              </button>
            </div>

            {error && <p className="text-sm text-red-600 mb-4 text-center">{error}</p>}

            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={uploading || !pendingFile}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-indigo-500 text-white hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {uploading ? 'Saving…' : 'Save Photo'}
              </button>
              <button
                onClick={handleClose}
                disabled={uploading}
                className="flex-1 py-2.5 rounded-xl font-semibold text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>

            {photoUrl && !pendingPreview && (
              <button
                onClick={handleRemove}
                disabled={uploading}
                className="w-full mt-3 py-2 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
              >
                Remove photo
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ProfilePhotoEditor;
